import { EMPTY, Subject, zip, type Observable, type ReplaySubject } from 'rxjs';
import { filter, map, switchMap, take, tap } from 'rxjs/operators';

import { createPusherMessageCache } from '../pusher/message-cache';
import { RATES_UPDATED } from '../../services/pusher-service/pusher-contract-messages';
import { type RatesUpdatedMessage, type RateShopperConfigurationRecommendation } from '../../types/pusher-messages/rates-updated';

type RateRefreshResult = {
  rateObject: { totalCost: { value: number } };
  fulfillmentPlanId: string;
  rateJobGuid: string;
};

export type RateShopperPromoData = {
  fulfillmentPlanId: string;
  isRating: boolean;
  serviceCollectionGuid: string;
  shippingServiceId: string;
  sellerProviderId: string;
  confirmationId: string;
  packageTypeId: string;
  savings: number;
  currencyCode: string;
};

export const rateShopperPromoSubject = new Subject<
  RateShopperPromoData | { fulfillmentPlanId: string; isRating: true; serviceCollectionGuid: string }
>();

const promoCache = new Map<string, RateShopperPromoData>();

export const getPromoRateForFulfillmentPlan = (fulfillmentPlanId: string): RateShopperPromoData | undefined =>
  promoCache.get(fulfillmentPlanId);

export const clearPromoRateForFulfillmentPlan = (fulfillmentPlanId: string): void => {
  promoCache.delete(fulfillmentPlanId);
};

export const storePromoRecommendation = (
  fulfillmentPlanId: string,
  recommendation: RateShopperConfigurationRecommendation,
  serviceCollectionGuid: string,
): void => {
  const promoData = {
    fulfillmentPlanId,
    isRating: false,
    savings: recommendation.savings,
    currencyCode: recommendation.recommendedRate.totalAmount?.currency,
    shippingServiceId: `${recommendation.recommendedShippingServiceID}`,
    sellerProviderId: `${recommendation.recommendedProviderID}`,
    confirmationId: '0',
    packageTypeId: '0',
    serviceCollectionGuid,
  };
  promoCache.set(fulfillmentPlanId, promoData);
  rateShopperPromoSubject.next(promoData);
};

const getRecommendationFromMessage = (
  pusherMessage: RatesUpdatedMessage,
  fulfillmentPlanId: string,
  rateJobGuid: string,
): RateShopperConfigurationRecommendation | null => {
  if (pusherMessage?.messageGuid !== rateJobGuid) {
    return null;
  }
  const order = pusherMessage?.data?.Orders.find((o) => `${o?.OrderID}` === fulfillmentPlanId);
  return order?.RateShopperConfigurationRecommendation ?? null;
};

export const watchForRateShopperPromo = (
  fulfillmentPlanId: string,
  serviceCollectionGuid: string,
  rateJobGuid: string,
  rateRefreshSubject: ReplaySubject<unknown>,
): Observable<never> => {
  if (!serviceCollectionGuid) return EMPTY;

  promoCache.delete(fulfillmentPlanId);
  rateShopperPromoSubject.next({ fulfillmentPlanId, isRating: true, serviceCollectionGuid });

  const { findMessage } = createPusherMessageCache([RATES_UPDATED]);

  return zip<[RateShopperConfigurationRecommendation, RateRefreshResult]>(
    findMessage((msg: RatesUpdatedMessage) => getRecommendationFromMessage(msg, fulfillmentPlanId, rateJobGuid) !== null).pipe(
      take(1),
      map(
        (pusherMessage: RatesUpdatedMessage) =>
          getRecommendationFromMessage(pusherMessage, fulfillmentPlanId, rateJobGuid) as RateShopperConfigurationRecommendation,
      ),
    ),
    rateRefreshSubject.pipe(
      filter((next): next is RateRefreshResult => {
        const n = next as Partial<RateRefreshResult>;
        return !!n?.rateObject && n.fulfillmentPlanId === fulfillmentPlanId && n.rateJobGuid === rateJobGuid;
      }),
      take(1),
    ),
  ).pipe(
    tap(([recommendation, rate]: [RateShopperConfigurationRecommendation, RateRefreshResult]) => {
      if (!recommendation.savings || !rate?.rateObject?.totalCost?.value) {
        return;
      }
      storePromoRecommendation(fulfillmentPlanId, recommendation, serviceCollectionGuid);
    }),
    switchMap(() => EMPTY),
  );
};
